import React, { useState, useEffect } from "react";
import { useAppContext } from "../AppContext";
import StripeFinalCodeCheck from "./StripeFinalCodeCheck";
import axios from "axios";

const API_BASE = "https://app.commercialcleaningleadsforyou.com";

/* ─── Static sample leads (real name + sq ft, everything else blurred) ─── */
const SAMPLE_LEADS = [
    {
        company: "Lakewood Office Park LLC",
        sqft: "14,200 sq ft",
        address: "████ ██████ Ave",
        phone: "(███) ███-████",
        contact: "J██████ M█████",
        website: "██████████.com",
    },
    {
        company: "Meridian Medical Group",
        sqft: "9,800 sq ft",
        address: "████ ██████ Blvd",
        phone: "(███) ███-████",
        contact: "S████ R█████",
        website: "███████████.com",
    },
    {
        company: "Sunrise Childcare Center",
        sqft: "6,500 sq ft",
        address: "████ ██ St Suite ████",
        phone: "(███) ███-████",
        contact: "A████ C████",
        website: "███████.com",
    },
    {
        company: "Pinnacle Property Management",
        sqft: "22,100 sq ft",
        address: "████ ████████ Dr",
        phone: "(███) ███-████",
        contact: "D███ W██████",
        website: "████████████.com",
    },
    {
        company: "Greenfield Dental Partners",
        sqft: "4,300 sq ft",
        address: "████ ██████ Rd",
        phone: "(███) ███-████",
        contact: "L██ T██████",
        website: "█████████.com",
    },
];

/* ─── Fallback when API returns no rows or errors ─── */
const STATIC_LEAD_COUNT = 1432;

/* Blur placeholder for Contact Info when only company/sqft are returned from API */
const CONTACT_BLUR_PLACEHOLDER = {
  address: "████ ██████ Ave",
  phone: "(███) ███-████",
  contact: "J██████ M█████",
};

/** Map a GoLeads API row (any column names) to preview shape { company, sqft, address, phone, contact, website }. */
function mapRowToPreview(row) {
  const get = (...keys) => {
    for (const k of keys) {
      const v = row[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };
  const sqftRaw = get("sqft", "sq_ft", "SqFt", "square_footage", "SquareFootage", "squarefootageactual") || "—";
  const sqft = /^\d+$/.test(String(sqftRaw)) ? `${Number(sqftRaw).toLocaleString()} sq ft` : sqftRaw;
  return {
    company: get("Company", "company_name", "company", "BusinessName") || "—",
    sqft,
    address: get("Address", "address", "street", "Address1") || CONTACT_BLUR_PLACEHOLDER.address,
    phone: get("PHONE", "phone", "Phone", "phone_number") || CONTACT_BLUR_PLACEHOLDER.phone,
    contact: get("Contact", "contact_name", "ContactName", "decision_maker", "name") || CONTACT_BLUR_PLACEHOLDER.contact,
    website: get("website", "Website", "url", "web") || "—",
  };
}

/* ─── Shared plan data ─── */
const PLANS = [
    { qty: 50, price: 39 },
    { qty: 200, price: 99 },
    { qty: 500, price: 199 },
];

const CONTENT = {
    en: {
        formTitle: "Commercial Cleaning Leads For You",
        seeRealLeads: "See Real Leads in Your Area Before You Buy",
        noCommitment: "No commitment. Check what's available in your ZIP code instantly.",
        checkAvailability: "Check Availability",
        noSpam: "🔒 We'll never spam you. One-time confirmation only.",
        checkLeadAvailability: "Check Cleaning Lead Availability",
        confirmInstantly: "We'll confirm your commercial cleaning lead availability",
        instantly: "INSTANTLY.",
        yourZipCode: "Your ZIP Code",
        zipPlaceholder: "e.g. 68154",
        yourEmail: "Your Email Address",
        emailPlaceholder: "you@example.com",
        checking: "Checking…",
        showMyLeads: "Show My Leads →",
        back: "← Back",
        leadsFound: "✅ Leads Found in Your Area!",
        leadsAroundZip: "Commercial Cleaning Leads around ZIP",
        listIncludes: "Your {count}-record list will include targeted leads like these:",
        noLeadsZip: "Sorry for the inconvenience. We don't have leads available for this ZIP code at the moment. Please try another ZIP or check back later.",
        fullDetailsUnlocked: "Full contact details unlocked after purchase",
        chooseListSize: "Choose your list size:",
        getMyLeads: "Get My {count} Leads — ${price}",
        changeZipEmail: "← Change ZIP / Email",
        company: "Company",
        sqFt: "Sq Ft",
        contactInfo: "Contact Info",
        leads: "Leads",
        zipInvalid: "Please enter a valid 5-digit ZIP code.",
        emailInvalid: "Please enter a valid email address.",
        loadError: "Could not load leads. Please try again.",
    },
    es: {
        formTitle: "Leads de limpieza comercial para ti",
        seeRealLeads: "Ve leads reales en tu área antes de comprar",
        noCommitment: "Sin compromiso. Consulta al instante qué hay disponible en tu código postal.",
        checkAvailability: "Verificar disponibilidad",
        noSpam: "🔒 Nunca te enviaremos spam. Solo una confirmación.",
        checkLeadAvailability: "Verificar disponibilidad de leads de limpieza",
        confirmInstantly: "Confirmaremos la disponibilidad de leads de limpieza comercial",
        instantly: "AL INSTANTE.",
        yourZipCode: "Tu código postal",
        zipPlaceholder: "ej. 68154",
        yourEmail: "Tu correo electrónico",
        emailPlaceholder: "tu@ejemplo.com",
        checking: "Verificando…",
        showMyLeads: "Mostrar mis leads →",
        back: "← Atrás",
        leadsFound: "✅ ¡Leads encontrados en tu área!",
        leadsAroundZip: "Leads de limpieza comercial cerca del código postal",
        listIncludes: "Tu lista de {count} registros incluirá leads dirigidos como estos:",
        noLeadsZip: "Disculpa las molestias. Por el momento no tenemos leads disponibles para este código postal. Intenta con otro código postal o vuelve más tarde.",
        fullDetailsUnlocked: "Los datos de contacto completos se desbloquean después de la compra",
        chooseListSize: "Elige el tamaño de tu lista:",
        getMyLeads: "Obtener mis {count} leads — ${price}",
        changeZipEmail: "← Cambiar código postal / correo",
        company: "Empresa",
        sqFt: "Pies²",
        contactInfo: "Info de contacto",
        leads: "Leads",
        zipInvalid: "Ingresa un código postal válido de 5 dígitos.",
        emailInvalid: "Ingresa un correo electrónico válido.",
        loadError: "No se pudieron cargar los leads. Intenta de nuevo.",
    },
};

/* ========================================================
   Lead Preview Table — compact modern design
   ======================================================== */
function LeadsPreviewTable({ leads, t }) {
    return (
        <>
            <style>{`
                @keyframes rowIn {
                    from { opacity: 0; transform: translateX(-8px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                .lead-row { animation: rowIn 0.32s ease both; }
                .lead-row:hover td { background: #f0fdf4; }
                .lead-row:hover .row-accent { opacity: 1 !important; }
            `}</style>

            <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        <col style={{ width: "42%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "38%" }} />
                    </colgroup>
                    <thead>
                        <tr style={{ background: "linear-gradient(90deg, #26A94A 0%, #1465B7 100%)" }}>
                            <th className="text-left px-3 py-2 text-xs font-bold text-white tracking-wide uppercase">
                                {t.company}
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-bold text-white tracking-wide uppercase">
                                {t.sqFt}
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-bold text-white tracking-wide uppercase">
                                <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3 opacity-80 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    {t.contactInfo}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((lead, i) => (
                            <tr
                                key={i}
                                className="lead-row cursor-default"
                                style={{ animationDelay: `${i * 60}ms` }}
                            >
                                <td className="relative px-3 py-2 text-xs font-semibold text-[#1465B7] border-t border-gray-100 truncate">
                                    <div
                                        className="row-accent absolute left-0 top-0 bottom-0 w-0.5 bg-[#26A94A] rounded-r"
                                        style={{ opacity: 0, transition: "opacity 0.2s" }}
                                    />
                                    {lead.company}
                                </td>
                                <td className="px-3 py-2 text-xs font-bold text-[#26A94A] border-t border-gray-100 whitespace-nowrap">
                                    {lead.sqft}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100 blur-[3.5px] select-none truncate">
                                    {lead.address} · {lead.phone} · {lead.contact}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

/* ========================================================
   Plan Selector Row (shared between step 2 preview & step 3)
   ======================================================== */
function PlanSelector({ leadsCount, setLeadsCount, setSelectedPrice, t }) {
    return (
        <div
            className="w-full bg-[#BFEAF2] border-[3px] border-[#26A94A] rounded-xl overflow-hidden grid grid-cols-3"
        >
            {PLANS.map((p, idx) => (
                <button
                    key={p.qty}
                    type="button"
                    onClick={() => {
                        setLeadsCount(p.qty);
                        setSelectedPrice(p.price * 100);
                    }}
                    className={`py-2 flex flex-col items-center justify-center transition cursor-pointer
            ${idx !== 0 ? "border-l-[3px] border-[#26A94A]" : ""}
            ${leadsCount === p.qty ? "bg-[#85C1CF]" : ""}
          `}
                >
                    <span className="text-lg font-extrabold leading-none text-[#EAFB55] drop-shadow-[1.5px_1.5px_0px_#2E7D32]">
                        {p.qty}
                    </span>
                    <span className="text-sm font-extrabold leading-none text-[#1465B7]">{t.leads}</span>
                    <span className="text-base sm:text-lg font-extrabold leading-none text-[#26A94A] my-1">
                        ${p.price}
                    </span>
                </button>
            ))}
        </div>
    );
}

/* ========================================================
   Mobile Pay Row
   ======================================================== */
function MobilePayRow({ onMobilePay }) {
    return (
        <div className="mt-4">
            <p className="text-center text-xs text-gray-500 mb-2 font-medium">
                — or pay instantly with —
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
                {/* Apple Pay */}
                <button
                    type="button"
                    onClick={() => onMobilePay("apple")}
                    className="flex items-center gap-1.5 bg-black text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow hover:bg-gray-900 active:scale-95 transition cursor-pointer"
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.28.07 2.17.8 2.92.82.93-.18 1.82-.85 2.84-.76 1.2.1 2.1.64 2.7 1.59-2.44 1.47-1.85 4.73.5 5.75-.58 1.53-1.33 3.06-2.96 4.46zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    Apple Pay
                </button>

                {/* Google Pay */}
                <button
                    type="button"
                    onClick={() => onMobilePay("google")}
                    className="flex items-center gap-1.5 bg-white text-gray-800 border border-gray-300 px-4 py-2.5 rounded-xl text-sm font-semibold shadow hover:bg-gray-50 active:scale-95 transition cursor-pointer"
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    Google Pay
                </button>

                {/* PayPal */}
                <button
                    type="button"
                    onClick={() => onMobilePay("paypal")}
                    className="flex items-center gap-1.5 bg-[#FFC439] text-[#003087] px-4 py-2.5 rounded-xl text-sm font-semibold shadow hover:bg-[#f0b429] active:scale-95 transition cursor-pointer"
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.379 1.56.637 4.006.637 4.006-1.107 4.512-4.708 5.957-9.358 5.957h-1.942l-1.086 6.871h2.83c.437 0 .808-.316.876-.748l.036-.184.694-4.403.044-.243a.876.876 0 0 1 .866-.748h.546c3.533 0 6.299-1.437 7.108-5.589.338-1.74.163-3.19-.644-4.378z" fill="#009cde" />
                    </svg>
                    PayPal
                </button>

                {/* Amazon Pay */}
                <button
                    type="button"
                    onClick={() => onMobilePay("amazon")}
                    className="flex items-center gap-1.5 bg-[#FF9900] text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow hover:bg-[#e88b00] active:scale-95 transition cursor-pointer"
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.23 17.22c.98 0 1.95-.32 2.71-.92.23-.18.53-.14.7.1.18.24.14.55-.1.73-.98.74-2.13 1.17-3.31 1.17-2.46 0-4.01-1.68-4.01-4.25 0-2.41 1.49-4.04 3.73-4.04 1.02 0 1.9.27 2.6.78.22.16.52.1.66-.12.14-.22.08-.52-.14-.68-.88-.65-1.96-1-3.12-1-2.98 0-5.1 2.27-5.1 5.24 0 3.08 2.14 5.24 5.1 5.24zM12.19 9.98c-.18-.23-.5-.27-.73-.09-1.43 1.12-3.38 1.69-5.49 1.69-.55 0-1.01-.45-1.01-1s.45-1 1.01-1c1.75 0 3.41-.48 4.73-1.34.24-.16.56-.1.72.14.16.24.1.56-.14.72-1.52 1.02-3.46 1.54-5.59 1.54-.55 0-1.01-.45-1.01-1s.45-1 1.01-1c1.71 0 3.35-.46 4.72-1.31.24-.16.56-.08.72.16.16.24.08.56-.16.72-1.56 1.05-3.54 1.58-5.56 1.58-.55 0-1-.45-1-1s.45-1 1-1c.95 0 1.89-.13 2.78-.38.24-.07.5.05.61.27.12.22.04.47-.18.59-.99.48-2.09.72-3.21.72-2.16 0-4.2-.57-5.93-1.64-.23-.14-.54-.07-.68.16z" />
                    </svg>
                    Amazon Pay
                </button>
            </div>
            <div className="mt-4">
            <p className="text-center text-sm text-gray-500 mt-1.5">
                Your chosen method will open on the next screen — complete payment there.
            </p>
            </div>
        </div>
    );
}

/* ========================================================
   MAIN: TwoStepCheckout
   step: 'cta' | 'form' | 'preview' | 'payment'
   ======================================================== */
export default function TwoStepCheckout({ lang = "en" }) {
    const t = CONTENT[lang] || CONTENT.en;
    const {
        leadsCount, setLeadsCount,
        selectedPrice, setSelectedPrice,
        buyNow, setBuyNow,
        zipcodes, setZipcodes, setEmail: setContextEmail,
        email: contextEmail,
        setPreferredPaymentMethod,
    } = useAppContext();

    // Local wizard step (separate from global buyNow)
    const [step, setStep] = useState(buyNow === 'payment' ? "payment" : "cta");
    const [zip, setZip] = useState("");
    const [email, setEmail] = useState("");
    const [zipError, setZipError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [checking, setChecking] = useState(false);
    const [goLeadsRows, setGoLeadsRows] = useState([]);
    const [goLeadsDisplayCount, setGoLeadsDisplayCount] = useState(null);
    const [goLeadsError, setGoLeadsError] = useState("");
    const [ipDetails, setIpDetails] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await axios.get("https://ipinfo.io/json");
                if (!cancelled) setIpDetails(response.data || null);
            } catch (e) {
                console.error("Failed to fetch IP location:", e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Sync with global state (e.g. if Header or Footer CTA is clicked)
    React.useEffect(() => {
        if (buyNow === 'payment') {
            setStep('payment');
        } else if (buyNow === 'form') {
            setStep('form'); // Open form when CTA clicked from bottom section
        } else if (buyNow === 'home') {
            setStep((prev) => (prev === 'payment' || prev === 'form' ? 'cta' : prev));
        }
    }, [buyNow]);

    // When opening the form step, pre-fill from context if available (e.g. after "Change ZIP/Email")
    React.useEffect(() => {
        if (step === "form" && (zipcodes?.trim() || contextEmail?.trim())) {
            if (zipcodes?.trim()) setZip(zipcodes.trim().replace(/\D/g, "").slice(0, 5));
            if (contextEmail?.trim()) setEmail(contextEmail.trim());
        }
    }, [step]); // Only when step becomes form; avoid overwriting while user types

    /* ── Validate & move from form → preview ── */
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        let valid = true;

        if (!/^\d{5}$/.test(zip.trim())) {
            setZipError(t.zipInvalid);
            valid = false;
        } else {
            setZipError("");
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setEmailError(t.emailInvalid);
            valid = false;
        } else {
            setEmailError("");
        }

        if (!valid) return;

        // Sync ZIP and email to global context so Stripe form is pre-filled
        setZipcodes(zip.trim());
        setContextEmail(email.trim());

        setChecking(true);
        setGoLeadsError("");
        try {
            // Run availability check and lead fetching in parallel
            const fetchCheckPromise = fetch(`${API_BASE}/api/check-availability`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email.trim(),
                    zip_code: zip.trim(),
                    ipDetails: ipDetails || undefined,
                }),
            }).catch(e => console.error("Failed to save check-availability:", e));

            const fetchLeadsPromise = fetch(
                `${API_BASE}/api/goleads-by-zip?zip=${encodeURIComponent(zip.trim())}&leads_count=${encodeURIComponent(leadsCount || 500)}`
            );

            const [, res] = await Promise.all([fetchCheckPromise, fetchLeadsPromise]);

            const data = await res.json();
            if (!res.ok) {
                setGoLeadsError(data?.error || `Request failed (${res.status})`);
                setGoLeadsRows([]);
                setGoLeadsDisplayCount(null);
            } else {
                const rows = Array.isArray(data) ? data : (data?.rows ?? []);
                const displayCount = data?.displayCount ?? (Array.isArray(data) ? data.length : null);
                setGoLeadsRows(rows);
                setGoLeadsDisplayCount(displayCount != null ? displayCount : null);
            }
        } catch (err) {
            setGoLeadsError(err?.message || t.loadError);
            setGoLeadsRows([]);
            setGoLeadsDisplayCount(null);
        } finally {
            setChecking(false);
            setStep("preview");
        }
    };

    /* ── Handle wallet/express pay: set preference first so create-payment-intent receives it, then go to payment ── */
    const handleMobilePay = (provider) => {
        setPreferredPaymentMethod(provider); // 'apple' | 'google' | 'paypal' | 'amazon'
        // Defer navigation so context commits preferredPaymentMethod before App.jsx effect runs
        setTimeout(() => {
            setStep("payment");
            setBuyNow("payment");
        }, 0);
    };

    /* ── Step: CTA ── */
    if (step === "cta") {
        return (
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-3 border-[#26A94A]">
                {/* Header badge */}
                <div className="flex">
                    <div className="flex-1 bg-[#26A94A] text-white p-4 font-semibold rounded-b-2xl text-center">
                        <span className="text-white text-lg">{t.formTitle}</span>
                    </div>
                </div>


                <div className="px-5 py-6 text-center">
                    <p className="text-lg font-bold text-[#1465B7] mb-2">
                        {t.seeRealLeads}
                    </p>
                    <p className="text-base text-gray-500 mb-6">
                        {t.noCommitment}
                    </p>

                    {/* Plan selector preview */}
                    <div className="flex justify-center mb-6">
                        <PlanSelector
                            leadsCount={leadsCount}
                            setLeadsCount={setLeadsCount}
                            setSelectedPrice={setSelectedPrice}
                            t={t}
                        />
                    </div>

                    <button
                        id="check-availability-btn"
                        className="w-full bg-[#FAF26B] hover:bg-yellow-300 text-[#1465B7] font-bold py-4 rounded-full transition-colors border-3 border-[#26A94A] cursor-pointer"
                        style={{ fontSize: "24px", color: "#1465B7" }}
                        onClick={() => setStep("form")}
                    >
                        {t.checkAvailability}
                    </button>

                    <div className="mt-4">
                    <p className="mt-3 text-xs text-gray-400">
                        {t.noSpam}
                    </p>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Step: Form (ZIP + Email) ── */
    if (step === "form") {
        return (
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-3 border-[#26A94A]">
                <div className="flex">
                    <button className="flex-1 bg-[#26A94A] text-white p-4 font-semibold rounded-b-2xl">
                        <span className="text-white text-lg">{t.checkAvailability}</span>
                    </button>
                </div>

                <form onSubmit={handleFormSubmit} className="px-5 py-6">
                    <p className="text-xl font-bold text-[#1465B7] mb-1 text-center">
                        {t.checkLeadAvailability}
                    </p>
                    <p className="text-sm text-gray-500 mb-5 text-center">
                        {t.confirmInstantly}{" "}
                        <strong className="text-[#26A94A]">{t.instantly}</strong>
                    </p>

                    {/* ZIP input */}
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-[#1465B7] mb-1" htmlFor="lead-zip">
                            {t.yourZipCode}
                        </label>
                        <input
                            id="lead-zip"
                            type="text"
                            inputMode="numeric"
                            maxLength={5}
                            placeholder={t.zipPlaceholder}
                            value={zip}
                            onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                            className={`w-full border-2 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-[#26A94A] transition ${zipError ? "border-red-400" : "border-[#26A94A]"
                                }`}
                        />
                        {zipError && (
                            <p className="text-red-500 text-xs mt-1">{zipError}</p>
                        )}
                    </div>

                    {/* Email input */}
                    <div className="mb-5">
                        <label className="block text-sm font-semibold text-[#1465B7] mb-1" htmlFor="lead-email">
                            {t.yourEmail}
                        </label>
                        <input
                            id="lead-email"
                            type="email"
                            placeholder={t.emailPlaceholder}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={`w-full border-2 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-[#26A94A] transition ${emailError ? "border-red-400" : "border-[#26A94A]"
                                }`}
                        />
                        {emailError && (
                            <p className="text-red-500 text-xs mt-1">{emailError}</p>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={checking}
                        className="w-full bg-[#FAF26B] hover:bg-yellow-300 text-[#1465B7] font-bold py-4 rounded-full transition-colors border-3 border-[#26A94A] cursor-pointer disabled:opacity-70"
                        style={{ fontSize: "18px" }}
                    >
                        {checking ? (
                            <span className="flex items-center justify-center gap-2" style={{color:'#1465B7'}} >
                                <span className="inline-block w-5 h-5 border-2 border-[#1465B7] border-t-transparent rounded-full animate-spin"/>
                                {t.checking}
                            </span>
                        ) : (
                            <span  style={{color:'#1465B7'}} >
                            {t.showMyLeads}
                            </span>
                        )}
                    </button>

<div className="mt-6">
                    <button
                        type="button"
                        onClick={() => { setStep("cta"); setBuyNow("home"); }}
                        className="w-full mt-2 text-xs text-gray-400 hover:text-gray-600 transition cursor-pointer"
                    >
                        {t.back}
                    </button>
                    </div>
                </form>
            </div>
        );
    }

    /* ── Step: Preview (leads table + payment CTA) ── */
    if (step === "preview") {
        return (
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-3 border-[#26A94A]">
                <div className="flex">
                    <div className="flex-1 bg-[#26A94A] text-white p-3 font-semibold rounded-b-2xl text-center">
                        <span className="text-white text-base">{t.leadsFound}</span>
                    </div>
                </div>

                <div className="px-4 py-4">
                    {/* Error from API */}
                    {goLeadsError && (
                        <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                            {goLeadsError}
                        </div>
                    )}

                    {/* Big count */}
                    <div className="text-center mb-3">
                        <p className="text-2xl font-extrabold text-[#1465B7]">
                            {(goLeadsDisplayCount != null ? goLeadsDisplayCount : (goLeadsRows.length > 0 ? goLeadsRows.length : STATIC_LEAD_COUNT)).toLocaleString()}
                        </p>
                        <p className="text-base text-gray-600">
                            {t.leadsAroundZip}{" "}
                            <strong className="text-[#26A94A]">{zip}</strong>
                        </p>
                        {goLeadsDisplayCount !== 0 && (
                            <p className="text-sm text-gray-400 mt-0.5">
                                {t.listIncludes.replace("{count}", leadsCount)}
                            </p>
                        )}
                    </div>

                    {/* When count is 0: show sorry message only; skip Company / Sq Ft table */}
                    {goLeadsDisplayCount === 0 ? (
                        <div className="mt-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-center">
                            <p className="text-amber-800 font-medium">
                                {t.noLeadsZip}
                            </p>
                        </div>
                    ) : (
                        <>
                            <LeadsPreviewTable
                                leads={
                                    goLeadsRows.length > 0
                                        ? goLeadsRows.slice(0, 5).map(mapRowToPreview)
                                        : SAMPLE_LEADS
                                }
                                t={t}
                            />

                    {/* Blur unlock note */}
                    <div className="mt-4 flex items-center gap-1.5 justify-center">
                        {/* <span className="text-base">🔓</span> */}
                        <p className="text-sm text-gray-500">
                            {t.fullDetailsUnlocked}
                        </p>
                    </div>
                        </>
                    )}

                    {/* Plan selector */}
                    <div className="mt-4 mb-4">
                        <p className="text-sm font-semibold text-[#1465B7] mb-2 text-center">
                            {t.chooseListSize}
                        </p>
                        <PlanSelector
                            leadsCount={leadsCount}
                            setLeadsCount={setLeadsCount}
                            setSelectedPrice={setSelectedPrice}
                            t={t}
                        />
                    </div>

                    {/* Main CTA */}
                    <button
                        id="get-my-leads-btn"
                        className="w-full bg-[#FAF26B] hover:bg-yellow-300 text-[#1465B7] font-bold py-4 rounded-full transition-colors border-3 border-[#26A94A] cursor-pointer"
                        style={{ fontSize: "20px", color:"#1465B7" }}
                        onClick={() => {
                            setBuyNow("payment");
                            setStep("payment");
                        }}
                    >
                        {t.getMyLeads
                            .replace("{count}", leadsCount)
                            .replace("${price}", PLANS.find(p => p.qty === leadsCount)?.price ?? 199)}
                    </button>

                    {/* Mobile pay */}
                    {/* <MobilePayRow onMobilePay={handleMobilePay} /> */}
                    <div className="my-4 text-sm">
                    <button
                        type="button"
                        onClick={() => setStep("form")}
                        className="w-full mt-3 text-xs text-gray-400 hover:text-gray-600 transition cursor-pointer"
                    >
                        {t.changeZipEmail}
                    </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Step: Payment (existing Stripe form) ── */
    if (step === "payment") {
        return <StripeFinalCodeCheck lang={lang} />;
    }

    return null;
}
